import axios from 'axios';
import { CONFIG } from '../config/config';
import { jwtDecode } from 'jwt-decode';
import { User, LoginResponse } from '../types';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

interface AuthProviderResponse {
    auth_provider: string;
}

const AUTH_TOKEN_KEY = 'auth_token';

// MSAL configuration
const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
        authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID}`,
        redirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'localStorage' as const,
        storeAuthStateInCookie: false,
    }
};

// Initialize MSAL instance
let msalInstance: PublicClientApplication | null = null;

const getMsalInstance = async () => {
    if (!msalInstance) {
        msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();
    }
    return msalInstance;
};

// Get the auth provider from the backend
export const getAuthProvider = async (): Promise<string> => {
    const response = await axios.get<AuthProviderResponse>(`${CONFIG.SERVER_URL}/get_auth_provider`);
    return response.data.auth_provider;
};

// Get the show multi-tenant clients flag from the backend
export const getShowMultiTenantClients = async (): Promise<boolean> => {
    const response = await axios.get<{ show_multi_tenant_clients: boolean }>(`${CONFIG.SERVER_URL}/get_show_multi_tenant_clients`);
    return response.data.show_multi_tenant_clients;
};

// Default authentication login
export const defaultAuthLogin = async (user_name: string, password: string, client_id?: string, license_key?: string) => {
    const response = await axios.post<LoginResponse>(`${CONFIG.SERVER_URL}/default_auth_login`, {
        user_name,
        password,
        client_id: client_id || null,  // Optional client/tenant identifier for multi-tenant support
        license_key: license_key || null  // License key for client validation
    });
    
    const { access_token } = response.data;
    localStorage.setItem(AUTH_TOKEN_KEY, access_token);
    return access_token;
};

// Azure AD authentication login
export const azureAuthLogin = async (): Promise<string> => {
    try {
        const msal = await getMsalInstance();
        
        const loginRequest = {
            scopes: ['User.Read']
        };
        
        // Try silent login first
        const accounts = msal.getAllAccounts();
        if (accounts.length > 0) {
            try {
                const response = await msal.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0]
                });
                
                // Send token to backend for validation and user creation
                const backendResponse = await axios.post<LoginResponse>(
                    `${CONFIG.SERVER_URL}/azure_auth_login`,
                    { access_token: response.accessToken }
                );
                
                const { access_token } = backendResponse.data;
                localStorage.setItem(AUTH_TOKEN_KEY, access_token);
                return access_token;
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    // Fall through to interactive login
                }
            }
        }
        
        // Interactive login
        const response = await msal.loginPopup(loginRequest);
        
        // Send token to backend
        const backendResponse = await axios.post<LoginResponse>(
            `${CONFIG.SERVER_URL}/azure_auth_login`,
            { access_token: response.accessToken }
        );
        
        const { access_token } = backendResponse.data;
        localStorage.setItem(AUTH_TOKEN_KEY, access_token);
        return access_token;
        
    } catch (error) {
        console.error('Azure AD login failed:', error);
        throw new Error('Azure AD authentication failed');
    }
};

// Main login function that determines which auth method to use
export const login = async (user_name?: string, password?: string, client_id?: string, license_key?: string) => {
    const authProvider = await getAuthProvider();
    
    switch (authProvider) {
        case 'default':
            if (!user_name || !password) {
                throw new Error('Username and password are required for default authentication');
            }
            return await defaultAuthLogin(user_name, password, client_id, license_key);
        
        case 'azure':
            return await azureAuthLogin();
        
        default:
            throw new Error(`Unsupported authentication provider: ${authProvider}`);
    }
};

export const logout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
};

export const getToken = () => localStorage.getItem(AUTH_TOKEN_KEY);

/** Replace the stored access token (e.g. after preference update returns a new JWT). */
export const setAuthToken = (token: string) => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
};

/**
 * Returns the app user embedded in the access token (JWT payload), including `roles`,
 * `attributes`, and `preferences` (e.g. `display_date_format`). No extra API call — decode only.
 */
export const getUser = (): User | null => {
    const token = getToken();
    if (!token) return null;
    return jwtDecode<User>(token);
};

/** Landing route from JWT `preferences.home_page` (see backend `ALLOWED_USER_PREFERENCES`). Used after login and for the title bar Home icon. */
export const getPostLoginPath = (): string => {
    const raw = getUser()?.preferences?.home_page?.trim();
    if (!raw) return '/home';
    switch (raw.toUpperCase()) {
        case 'DASHBOARD':
            return '/dashboard';
        case 'METRICS':
            return '/view-metrics';
        case 'REVIEWS':
            return '/review-management';
        case 'DEFAULT':
        default:
            return '/home';
    }
};

export const isAuthenticated = () => !!getToken();